import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { FormFieldWithValues } from '@/types/form';
import { FORM_FIELD_TYPE_LABELS } from '@/types/form';

interface FormFieldCardProps {
  field: FormFieldWithValues;
  onDelete?: (field: FormFieldWithValues) => void;
  /** Mode lecture seule - désactive le drag & drop */
  disabled?: boolean;
}

/**
 * Carte représentant un champ de formulaire avec drag & drop
 * Design cohérent avec le reste de l'application (zinc palette)
 */
export function FormFieldCard({ field, onDelete, disabled }: FormFieldCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group bg-white border border-zinc-200 rounded-md px-3 py-2.5 flex items-center gap-3 hover:border-zinc-300 hover:shadow-sm transition-all"
    >
      {/* Drag Handle - masqué en mode lecture seule */}
      {!disabled && (
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-600 transition-colors p-0.5"
          {...attributes}
          {...listeners}
          aria-label="Réorganiser le champ"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      {/* Field Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-zinc-900">{field.label}</span>

          {field.is_required && (
            <Badge variant="destructive" className="text-xs h-5">
              Requis
            </Badge>
          )}

          <Badge variant="secondary" className="text-xs h-5">
            {FORM_FIELD_TYPE_LABELS[field.field_type]}
          </Badge>
        </div>

        {/* Informations secondaires */}
        <div className="mt-0.5 flex flex-col gap-0.5">
          {field.placeholder && (
            <p className="text-xs text-zinc-500">Placeholder : {field.placeholder}</p>
          )}

          {field.options && field.options.length > 0 && (
            <p className="text-xs text-zinc-500">
              Options : {field.options.slice(0, 3).join(', ')}
              {field.options.length > 3 && ` (+${field.options.length - 3})`}
            </p>
          )}
        </div>
      </div>

      {/* Delete button */}
      {onDelete && (
        <button
          type="button"
          onClick={() => onDelete(field)}
          className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center h-7 w-7 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
          aria-label="Supprimer le champ"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
